import { computed, effectScope, watch } from 'vue';
import {
  audioSettings,
  loadUserPreferences,
  preferencesLoaded,
  preferencesLoading,
  resetAudioSettings,
  setSoundSettings,
  setSoundsSettings,
} from '../utils/user_preferences';
import { applyAudioSettings, resolveAudioSettings } from '../utils/matchmakingSounds';

// By role, changes being tried out and not yet saved, as while a volume slider is dragged. Laid
// over the stored settings, so a preferences response landing mid-drag does not snap it back.
let trying = {};

function withChanges(settings, changes) {
  const sounds = { ...settings.sounds };
  Object.entries(changes).forEach(([role, change]) => {
    sounds[role] = { ...sounds[role], ...change };
  });
  return { ...settings, sounds };
}

// The last audio settings the account answered with, for a load that fails: the account is the only
// other copy of a mute, and a failed load must not unmute everything. Sign-out sweeps it.
const CACHE_KEY = 'audioSettings';

function readCache() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
  } catch {
    return {};
  }
}

function writeCache(settings) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(settings));
  } catch {
    // Storage blocked: a failed load falls back to the defaults
  }
}

// The stored settings, or after a failed load the cached ones, unless this tab has set some since
function storedSettings() {
  if (preferencesLoaded.value) return audioSettings.value;
  return Object.keys(audioSettings.value).length ? audioSettings.value : readCache();
}

const applyCurrent = () => applyAudioSettings(withChanges(storedSettings(), trying));

// One watcher for the whole app, however many components use this, kept in its own scope so it
// outlives the first of them. The sounds take the settings up once the preferences have loaded,
// or failed to, and as they change after that. Loaded here too (once per session), so a page
// without the dashboard layout still gets there.
let syncing = false;
function sync() {
  if (syncing) return;
  syncing = true;
  loadUserPreferences();
  effectScope(true).run(() => {
    watch(
      // preferencesLoaded too: a write landing after a failed load replaces the cached settings
      // even when the account's JSON reads the same
      () => !preferencesLoading.value
        && `${preferencesLoaded.value}:${JSON.stringify(audioSettings.value)}`,
      (current) => {
        if (!current) return;
        if (preferencesLoaded.value) writeCache(audioSettings.value);
        applyCurrent();
      },
      { immediate: true },
    );
  });
}

// The player's audio settings, with every default filled in, and changing them.
export default function useAudioSettings() {
  sync();

  const settings = computed(() => resolveAudioSettings(storedSettings()));

  // Hears a change before it is saved. Not before the settings have loaded: the sounds would take
  // up defaults, mutes and all, and let go of what they hold for the real ones.
  function tryOut(role, changes) {
    trying = { ...trying, [role]: { ...trying[role], ...changes } };
    if (!preferencesLoading.value) applyCurrent();
  }

  // Saves a change, ending any try of it.
  function updateSound(role, changes) {
    const { [role]: tried, ...rest } = trying;
    trying = rest;
    return setSoundSettings(role, { ...tried, ...changes });
  }

  // Saves a change to several roles in one write, ending any try of them. `changes` is the same
  // for each, or a function of the role.
  function updateSounds(roles, changes) {
    const of = (role) => (typeof changes === 'function' ? changes(role) : changes);
    const byRole = Object.fromEntries(
      roles.map((role) => [role, { ...trying[role], ...of(role) }]),
    );
    trying = Object.fromEntries(Object.entries(trying).filter(([role]) => !roles.includes(role)));
    return setSoundsSettings(byRole);
  }

  function restoreDefaults() {
    trying = {};
    return resetAudioSettings();
  }

  return {
    settings, updateSound, updateSounds, restoreDefaults, tryOut,
  };
}
